import type {
  DataLayerType,
  SearchShelfCardSection,
  TTutorialPlatform,
  TTutorialTrigger,
  TYarikanoCtaPlacement,
} from 'types';

type TrackSearchShelfCardParams = {
  cardId: number;
  cardTitle: string;
  section: SearchShelfCardSection;
  userId?: number;
};

type TrackUserIdParams = {
  userId?: number;
};

type TrackShelfTabViewParams = TrackUserIdParams & {
  section: SearchShelfCardSection;
};

type TrackYarikanoCardParams = TrackUserIdParams & {
  yarikanoGirlId: number;
  rank: number;
};

type TrackQuickReplyClickParams = TrackUserIdParams & {
  questionId: string;
};

type TrackYarikanoChatChipClickParams = TrackUserIdParams & {
  questionId: string;
};

type TrackYarikanoChatCtaClickParams = TrackUserIdParams & {
  questionId: string;
  placement: TYarikanoCtaPlacement;
};

type TrackPwaGuideParams = TrackUserIdParams & {
  trigger: TTutorialTrigger;
  platform: TTutorialPlatform;
};

const toUserIdLabel = (userId?: number): string =>
  userId != null ? `${userId}` : '';

export const pushDataLayer = (data: DataLayerType): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

export const trackSearchShelfCardClick = ({
  cardId,
  cardTitle,
  section,
  userId,
}: TrackSearchShelfCardParams): void => {
  pushDataLayer({
    event: 'shelf_card_click',
    card_id: cardId,
    card_title: cardTitle,
    section,
    user_id: toUserIdLabel(userId),
  });
};

export const trackSearchShelfCardImpression = ({
  cardId,
  cardTitle,
  section,
  userId,
}: TrackSearchShelfCardParams): void => {
  pushDataLayer({
    event: 'shelf_card_impression',
    card_id: cardId,
    card_title: cardTitle,
    section,
    user_id: toUserIdLabel(userId),
  });
};

export const trackSearchPageView = ({ userId }: TrackUserIdParams): void => {
  pushDataLayer({
    event: 'search_page_view',
    page_path:
      typeof window !== 'undefined' ? window.location.pathname : '/search',
    user_id: toUserIdLabel(userId),
  });
};

export const trackShelfTabView = ({
  section,
  userId,
}: TrackShelfTabViewParams): void => {
  pushDataLayer({
    event: 'shelf_tab_view',
    section,
    user_id: toUserIdLabel(userId),
  });
};

export const trackYarikanoCardImpression = ({
  yarikanoGirlId,
  rank,
  userId,
}: TrackYarikanoCardParams): void => {
  pushDataLayer({
    event: 'yarikano_card_impression',
    yarikano_girl_id: yarikanoGirlId,
    rank,
    user_id: toUserIdLabel(userId),
  });
};

export const trackYarikanoCardClick = ({
  yarikanoGirlId,
  rank,
  userId,
}: TrackYarikanoCardParams): void => {
  pushDataLayer({
    event: 'yarikano_card_click',
    yarikano_girl_id: yarikanoGirlId,
    rank,
    user_id: toUserIdLabel(userId),
  });
};

export const trackQuickReplyClick = ({
  questionId,
  userId,
}: TrackQuickReplyClickParams): void => {
  pushDataLayer({
    event: 'quick_reply_click',
    question_id: questionId,
    user_id: toUserIdLabel(userId),
  });
};

export const trackYarikanoChatOpen = ({ userId }: TrackUserIdParams): void => {
  pushDataLayer({
    event: 'yarikano_chat_open',
    user_id: toUserIdLabel(userId),
  });
};

export const trackYarikanoChatChipClick = ({
  questionId,
  userId,
}: TrackYarikanoChatChipClickParams): void => {
  pushDataLayer({
    event: 'yarikano_chat_chip_click',
    question_id: questionId,
    user_id: toUserIdLabel(userId),
  });
};

export const trackYarikanoChatCtaClick = ({
  questionId,
  placement,
  userId,
}: TrackYarikanoChatCtaClickParams): void => {
  pushDataLayer({
    event: 'yarikano_chat_cta_click',
    question_id: questionId,
    placement,
    user_id: toUserIdLabel(userId),
  });
};

export const trackPwaGuideOpen = ({
  trigger,
  platform,
  userId,
}: TrackPwaGuideParams): void => {
  pushDataLayer({
    event: 'pwa_guide_open',
    trigger,
    platform,
    user_id: toUserIdLabel(userId),
  });
};

export const trackPwaGuideClose = ({
  trigger,
  platform,
  userId,
}: TrackPwaGuideParams): void => {
  pushDataLayer({
    event: 'pwa_guide_close',
    trigger,
    platform,
    user_id: toUserIdLabel(userId),
  });
};

export const trackPwaStandaloneLaunch = ({
  userId,
}: TrackUserIdParams): void => {
  pushDataLayer({
    event: 'pwa_standalone_launch',
    user_id: toUserIdLabel(userId),
  });
};
